import React, { FC, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import * as customersActions from "../../../store/actions/customersActions";
import { Customer } from "../../auth/Auth";
import { CustomerErrors, validateForm } from "./validationUtils";
import Input from "../../UI/Input";
import Button from "../../UI/Button";
import { Modal, ModalClose, ModalDialog, Typography } from "@mui/joy";
import { Box, FormControlLabel, Grid } from "@mui/material";
import { Save } from "@mui/icons-material";
import Checkbox from "../../UI/Checkbox";

interface CustomerModalProps {
    open: boolean;
    selectedCustomer: Customer | null;
    onHandleClose: () => void;
}

const CustomerModal: FC<CustomerModalProps> = ({ open, selectedCustomer, onHandleClose }) => {
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [errors, setErrors] = useState<CustomerErrors>({
        name: "",
        address: "",
        email: "",
        phoneNumber: ""
    });

    const dispatch = useDispatch();
    const { addCustomer } = bindActionCreators(customersActions, dispatch);

    useEffect(() => {
        if (selectedCustomer) {
            setCustomer(selectedCustomer);
        } else {
            setCustomer({
                id: "",
                name: "",
                address: "",
                email: "",
                phoneNumber: "",
                deliveryTime: "09:00",
                canOrderCurrentDay: false,
                identificationNumber: "",
                subvention: 0,
                taxId: "",
            });
        }
    }, [selectedCustomer]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;

        setCustomer((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [name]: e.target.type === "checkbox" ? e.target.checked : value
            };
        });

        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleAddOrEditUser = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        const { valid, errors } = validateForm(customer);
        if (!valid) {
            setErrors(errors);
        } else {
            const isAddMode = selectedCustomer === null;
            addCustomer(customer, isAddMode);
            onHandleClose();
        }
    };

    return (
        <Modal open={open} onClose={onHandleClose}>
            <ModalDialog
                aria-labelledby="basic-modal-dialog-title"
                aria-describedby="basic-modal-dialog-description"
                sx={{ minWidth: 400, overflow: "auto" }}
            >
                <ModalClose variant="outlined" />
                <Typography id="basic-modal-dialog-title" level="h2">
                    {selectedCustomer ? "Dodaj novu kompaniju" : "Izmeni kompaniju"}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="Naziv"
                            type="text"
                            value={customer?.name}
                            name="name"
                            required
                            error={errors.name}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="Adresa"
                            type="text"
                            value={customer?.address}
                            name="address"
                            required
                            error={errors.address}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="Email"
                            type="text"
                            value={customer?.email}
                            name="email"
                            required
                            error={errors.email}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="Telefon"
                            type="text"
                            value={customer?.phoneNumber}
                            name="phoneNumber"
                            required
                            error={errors.phoneNumber}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="Vreme dostave"
                            type="time"
                            value={customer?.deliveryTime || "09:00"}
                            name="deliveryTime"
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="Subvencija"
                            type="number"
                            value={customer?.subvention || 0}
                            name="subvention"
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="Matični broj"
                            type="text"
                            value={customer?.identificationNumber}
                            name="identificationNumber"
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Input
                            label="PIB"
                            type="text"
                            value={customer?.taxId}
                            name="taxId"
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={customer?.canOrderCurrentDay}
                                    onChange={handleInputChange}
                                    name="canOrderCurrentDay"
                                />
                            }
                            label="Dozvoli poručivanje za isti dan"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ textAlign: "right" }}>
                            <Button
                                label="Sačuvaj"
                                startIcon={<Save />}
                                className="btn_success"
                                handleClick={handleAddOrEditUser}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </ModalDialog>
        </Modal>
    );
};

export default CustomerModal;